import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonDirective, SpinnerComponent } from '@coreui/angular-pro';
import { I18nService, TranslatePipe } from 'i18n';

import { CompanionOverview, HealthCompanionService } from '../../../services/health-companion.service';
import { CompanionTodayComponent } from './companion-today.component';
import { CompanionDiaryComponent } from './companion-diary.component';
import { CompanionProfileComponent } from './companion-profile.component';
import { CompanionSettingsComponent } from './companion-settings.component';

export type CompanionTab = 'today' | 'diary' | 'profile' | 'settings';
const TABS: CompanionTab[] = ['today', 'diary', 'profile', 'settings'];

/**
 * "Meine Begleitung" — the daily companion of the DNA & Health SPECIAL
 * (docs/globalhealth/TAEGLICHE_BEGLEITUNG_PLAN.md §7 + §13). One page, four
 * tabs: Heute (check-in, plan, chat), Tagebuch (days, values, blood pressure),
 * Profil (goals, frame, age check, guardian), Einstellungen (AI switch, export,
 * documents, deletion).
 *
 * The shell loads the overview and routes the member by state: no entitlement →
 * explanation; not yet a profile / age not checked → the profile tab; a minor
 * without a confirmed guardian consent → the profile tab with the guardian
 * section; expired → read-only notice, diary stays open.
 */
@Component({
  selector: 'app-companion-page',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe, ButtonDirective, SpinnerComponent,
            CompanionTodayComponent, CompanionDiaryComponent, CompanionProfileComponent, CompanionSettingsComponent],
  templateUrl: './companion-page.component.html',
  styleUrls: ['./companion-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanionPageComponent {
  private companion  = inject(HealthCompanionService);
  private i18n       = inject(I18nService);
  private route      = inject(ActivatedRoute);
  private router     = inject(Router);
  private destroyRef = inject(DestroyRef);

  readonly tabs = TABS;
  loading  = signal(true);
  error    = signal<string | null>(null);
  overview = signal<CompanionOverview | null>(null);
  tab      = signal<CompanionTab>('today');
  lang     = signal(this.i18n.getLanguage());

  entitlement = computed(() => this.overview()?.entitlement.status ?? 'none');
  profile     = computed(() => this.overview()?.profile ?? null);
  /** The profile must be confirmed (and the age checked) before the companion works. */
  needsProfile = computed(() => {
    const p = this.profile();
    return !p || !p.age_verified || !p.confirmed;
  });
  readOnly = computed(() => this.entitlement() === 'expired');
  locked   = computed(() => this.entitlement() === 'minor_consent_required');

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const t = params.get('tab') as CompanionTab | null;
      if (t && TABS.includes(t)) this.tab.set(t);
    });
    this.i18n.currentLanguage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(l => this.lang.set(l));
    this.reload();
    // Without a confirmed profile (or while a minor waits for the guardian) the
    // companion cannot start: every tab lands on the profile until that is done.
    effect(() => {
      const o = this.overview();
      if (!o || o.entitlement.status === 'none') return;
      if ((this.needsProfile() || this.locked()) && this.tab() !== 'profile') this.tab.set('profile');
    }, { allowSignalWrites: true });
  }

  reload(): void {
    this.companion.overview().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: o => { this.overview.set(o); this.loading.set(false); this.error.set(null); },
      error: err => {
        this.loading.set(false);
        const code = err?.error?.error;
        this.error.set(code === 'companion_disabled' ? 'disabled' : code === 'not_found' ? 'not_found' : 'error');
      }
    });
  }

  select(tab: CompanionTab): void {
    this.tab.set(tab);
    this.router.navigate(['/', this.lang(), 'companion', tab], { replaceUrl: true });
  }

  daysLeft(): number { return this.overview()?.entitlement.days_left ?? 0; }

  // ----- time travel (dev/staging only; the backend answers 404 elsewhere) -----------

  clockBusy = signal(false);
  clock = computed(() => this.overview()?.clock ?? null);

  shiftClock(days: number): void {
    const c = this.clock();
    if (!c) return;
    const d = new Date(`${c.today}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    this.setClock(d.toISOString().slice(0, 10));
  }

  setClock(date: string | null): void {
    if (this.clockBusy() || (date !== null && !/^\d{4}-\d{2}-\d{2}$/.test(date))) return;
    this.clockBusy.set(true);
    this.companion.setClock(date).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.clockBusy.set(false); this.reload(); },
      error: () => this.clockBusy.set(false)
    });
  }

  /** 1-based onboarding step for the notice: age = 1, then the seven profile steps. */
  onboardingStep(): number {
    const p = this.profile();
    if (!p || !p.age_verified) return 1;
    const steps = ['goals', 'baseline', 'time', 'movement', 'restrictions', 'settings', 'review'];
    const i = steps.indexOf(p.onboarding_step);
    return i < 0 ? 2 : i + 2;
  }
}
